import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaGavel, FaMapMarkerAlt } from "react-icons/fa";
import { resolveHubAuctionImage } from "../../utils/b2bHubHomeMedia";
import {
  buildAuctionSpecItems,
  canShowAuctionBidNow,
  getAuctionCategoryTrail,
  getAuctionDetailHref,
  getAuctionImagePath,
  getAuctionLocation,
  getAuctionShareImage,
  getAuctionStartingPriceLabel,
  getAuctionStatusChips,
  resolveAuctionRowBadge,
} from "../../utils/auctionCardHelpers";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

export default function AuctionListingListRow({ auction, onToggleCompare, compared = false }) {
  const detailHref = getAuctionDetailHref(auction);
  const imagePath = getAuctionImagePath(auction);
  const location = getAuctionLocation(auction);
  const priceLabel = getAuctionStartingPriceLabel(auction);
  const specItems = buildAuctionSpecItems(auction).filter((spec) => !spec.empty);
  const badge = resolveAuctionRowBadge(auction);
  const categoryTrail = getAuctionCategoryTrail(auction);
  const statusChips = getAuctionStatusChips(auction);
  const sellerName = auction.seller_company || auction.seller_name || "Auction Seller";
  const showBidNow = canShowAuctionBidNow(auction);

  return (
    <article className="marketplace-product-row marketplace-product-row--auction group">
      <div className="marketplace-product-row__media">
        {badge ? (
          <span className={`marketplace-product-row__badge ${badge.className}`}>{badge.label}</span>
        ) : null}
        <Link to={detailHref} className="marketplace-product-row__media-link">
          {imagePath ? (
            <img
              src={resolveHubAuctionImage(imagePath, auction.auction_id)}
              alt={auction.product_name}
              loading="lazy"
            />
          ) : (
            <div className="marketplace-product-row__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-row__body">
        <div className="marketplace-product-row__head">
          <div className="marketplace-product-row__head-left">
            <Link to={detailHref} className="marketplace-product-row__title">
              {auction.product_name}
            </Link>

            {categoryTrail.length ? (
              <p className="marketplace-product-row__category-trail">{categoryTrail.join(" › ")}</p>
            ) : null}

            <div className="marketplace-product-row__seller">
              <span className="marketplace-product-row__company">{sellerName}</span>
              {location ? (
                <span className="marketplace-product-row__location">
                  <FaMapMarkerAlt aria-hidden /> {location}
                </span>
              ) : null}
            </div>
          </div>

          <div className="marketplace-product-row__head-right">
            {priceLabel !== "—" ? <p className="marketplace-product-row__price">{priceLabel}</p> : null}
          </div>
        </div>

        {specItems.length ? (
          <dl
            className="marketplace-product-row__specs"
            style={{ gridTemplateColumns: `repeat(${Math.min(specItems.length, 4)}, minmax(0, 1fr))` }}
          >
            {specItems.map((spec) => (
              <div key={`${spec.label}-${spec.value}`} className="marketplace-product-row__spec">
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className={`marketplace-product-row__meta${statusChips.length ? "" : " marketplace-product-row__meta--empty"}`}>
          {statusChips.map((chip) => (
            <span key={chip} className="marketplace-product-row__cert">
              {chip}
            </span>
          ))}
        </div>

        <div className="marketplace-product-row__actions">
          {showBidNow ? (
            <Link to={detailHref} className="marketplace-product-row__btn marketplace-product-row__btn--primary">
              <FaGavel aria-hidden /> Bid Now
            </Link>
          ) : (
            <Link to={detailHref} className="marketplace-product-row__btn marketplace-product-row__btn--primary">
              View Auction
            </Link>
          )}
          <Link to={detailHref} className="marketplace-product-row__btn marketplace-product-row__btn--outline-dark">
            View Details
          </Link>
          <button
            type="button"
            className={`marketplace-product-row__btn marketplace-product-row__btn--outline${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(auction.auction_id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={auction.auction_id}
            title={auction.product_name}
            sharePath={detailHref}
            imageUrl={getAuctionShareImage(auction)}
            companyName={sellerName}
            description={auction.product_description || `Live auction listing${location ? ` in ${location}` : ""}`}
            triggerClassName="marketplace-product-row__btn marketplace-product-row__btn--outline marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
